import { SUBJECTS, SUBJECT_FALLBACK } from './config'

export const ENGINE_VERSION = '1.0.0'

export type PlanBlockType = 'ders' | 'quiz' | 'tekrar' | 'deneme' | 'yanlis' | 'kelime'

export type PlanDayKind = 'normal' | 'deneme' | 'tekrar'

export interface TypeMeta {
  icon: string
  label: string
}

export interface SubjectMeta {
  icon: string
  cls: string
}

export interface PlanArea {
  label: string
  cycle: string[]
}

export interface StudyPlanConfig {
  examDate: string
  areas: Record<string, PlanArea>
  goalBoost: Record<string, string[]>
}

export interface PlanState {
  area: string
  goal: string
  hours: string
}

export interface PlanBlock {
  type: PlanBlockType
  subject: string
  mins: number
  session?: number
  light?: boolean
}

export interface PlanGroup {
  label: string | null
  blocks: PlanBlock[]
}

export interface PlanDay {
  day: string
  kind: PlanDayKind
  blocks: PlanBlock[]
  groups: PlanGroup[]
}

export type PlanWeek = PlanDay[]

export interface PlanStats {
  hours: string
  perWeek: string
  ders: number
  quiz: number
  deneme: number
}

export interface PlanResult {
  countdown: number
  week: PlanWeek
  stats: PlanStats
  focus: string
}

export const DAY_NAMES: readonly string[] = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz']

export const DAY_PARTS: readonly string[] = ['🌅 Sabah', '☀️ Öğleden sonra', '🌙 Akşam']

export const TYPE_META: Record<PlanBlockType, TypeMeta> = {
  ders: { icon: '📖', label: 'Ders' },
  quiz: { icon: '✏️', label: 'Soru çözümü' },
  tekrar: { icon: '↻', label: 'Tekrar' },
  deneme: { icon: '🎯', label: 'Deneme' },
  yanlis: { icon: '🔍', label: 'Yanlış analizi' },
  kelime: { icon: '🔤', label: 'Kelime tekrarı' },
}

export function subjMeta(name: string): SubjectMeta {
  return SUBJECTS[name] || SUBJECT_FALLBACK
}

export function daysUntil(iso: string): number {
  const ms = new Date(iso + 'T09:00:00').getTime() - Date.now()
  return Math.max(0, Math.ceil(ms / 86400000))
}

export function slotsPerDay(hours: string): number {
  if (hours === '1 saat') return 1
  if (hours === '2 saat') return 2
  if (hours === '4+ saat') return 4
  if (hours === '6 saat') return 6
  if (hours === '8 saat') return 8
  if (hours === '10 saat') return 10
  return 3
}

export function slotMinutes(hours: string): number {
  if (hours === '1 saat') return 60
  if (hours === '2 saat') return 50
  if (hours === '6 saat' || hours === '8 saat' || hours === '10 saat') return 40
  return 45
}

export function weeklyHours(hours: string): string {
  const per: Record<string, string> = {
    '1 saat': '7',
    '2 saat': '14',
    '3 saat': '21',
    '4+ saat': '28+',
    '6 saat': '42',
    '8 saat': '56',
    '10 saat': '70',
  }
  return `${per[hours] || '21'} saat`
}

export function hoursOption(value: number, options: readonly string[]): string {
  return options.find((o) => parseInt(o, 10) === value) || ''
}

function dayPartSizes(n: number): number[] | null {
  if (n <= 3) return null
  if (n === 4) return [2, 2]
  if (n === 6) return [2, 2, 2]
  if (n === 8) return [2, 3, 3]
  return [3, 4, 3]
}

export function buildWeek(state: PlanState, config: StudyPlanConfig): PlanWeek {
  const area = config.areas[state.area]
  const boost = config.goalBoost[state.goal] || []
  const cycle = [...boost, ...area.cycle]
  const n = slotsPerDay(state.hours)
  const mins = slotMinutes(state.hours)
  const week: PlanWeek = []
  let c = 0
  const pick = (): string => cycle[c++ % cycle.length]
  for (let d = 0; d < 7; d++) {
    if (d === 5) {
      const heavy = slotsPerDay(state.hours) >= 6
      const blocks: PlanBlock[] = [
        { type: 'deneme', subject: state.area === 'dil' ? 'YDT denemesi' : 'TYT denemesi', mins: heavy ? 160 : 135 },
        { type: 'yanlis', subject: 'Deneme analizi', mins: heavy ? 60 : 45 },
      ]
      week.push({ day: DAY_NAMES[d], kind: 'deneme', blocks, groups: [{ label: null, blocks }] })
      continue
    }
    if (d === 6) {
      const heavy = slotsPerDay(state.hours) >= 6
      const blocks: PlanBlock[] = [
        { type: 'tekrar', subject: 'Haftanın konuları', mins: heavy ? 120 : 60 },
        { type: 'kelime', subject: 'Kelime / formül kartları', mins: heavy ? 60 : 30 },
      ]
      week.push({ day: DAY_NAMES[d], kind: 'tekrar', blocks, groups: [{ label: null, blocks }] })
      continue
    }
    const blocks: PlanBlock[] = []
    for (let s = 0; s < n; s++) {
      const isQuiz = n === 2 ? s === 1 : ((s + 1) % 3 === 0 || s === n - 1)
      blocks.push({ type: isQuiz ? 'quiz' : 'ders', subject: pick(), mins, session: s + 1 })
    }
    if (n === 1) blocks.push({ type: 'tekrar', subject: 'Dünkü konu', mins: 20, light: true })
    const sizes = dayPartSizes(n)
    const groups: PlanGroup[] = sizes
      ? (() => {
          let k = 0
          return sizes.map((sz, i) => ({ label: DAY_PARTS[i], blocks: blocks.slice(k, (k += sz)) }))
        })()
      : [{ label: null, blocks }]
    week.push({ day: DAY_NAMES[d], kind: 'normal', blocks, groups })
  }
  return week
}

export function planStats(state: PlanState, week: PlanWeek): PlanStats {
  let ders = 0
  let quiz = 0
  week.forEach(d =>
    d.blocks.forEach(b => {
      if (b.type === 'ders') ders++
      if (b.type === 'quiz') quiz++
    })
  )
  return {
    hours: state.hours,
    perWeek: weeklyHours(state.hours),
    ders,
    quiz,
    deneme: 1,
  }
}

export function buildPlan(state: PlanState, config: StudyPlanConfig): PlanResult {
  const week = buildWeek(state, config)
  return {
    countdown: daysUntil(config.examDate),
    week,
    stats: planStats(state, week),
    focus: config.areas[state.area].label,
  }
}
